import { useEffect, useState } from 'react';

export type FloorPlanMediaType = 'IMAGE' | 'VIDEO';

export type LoadedFloorPlanMedia =
    | {
          type: 'IMAGE';
          naturalWidth: number;
          naturalHeight: number;
      }
    | {
          type: 'VIDEO';
          naturalWidth: number;
          naturalHeight: number;
      }
    | null;

export const useFloorPlanMedia = (url: string, mediaType?: FloorPlanMediaType) => {
    const [media, setMedia] = useState<LoadedFloorPlanMedia>(null);

    useEffect(() => {
        if (!url || !mediaType) {
            setMedia(null);
            return;
        }

        let cancelled = false;

        if (mediaType === 'IMAGE') {
            const img = new Image();
            img.src = url;

            img.onload = () => {
                if (cancelled) return;
                setMedia({
                    type: 'IMAGE',
                    naturalWidth: img.naturalWidth,
                    naturalHeight: img.naturalHeight,
                });
            };

            img.onerror = () => {
                if (cancelled) return;
                setMedia(null);
            };

            return () => {
                cancelled = true;
            };
        }

        const video = document.createElement('video');
        video.src = url;
        video.preload = 'metadata';
        video.playsInline = true;
        video.muted = true;

        const handleLoadedMetadata = () => {
            if (cancelled) return;
            setMedia({
                type: 'VIDEO',
                naturalWidth: video.videoWidth,
                naturalHeight: video.videoHeight,
            });
        };

        const handleError = () => {
            if (cancelled) return;
            setMedia(null);
        };

        video.addEventListener('loadedmetadata', handleLoadedMetadata);
        video.addEventListener('error', handleError);

        return () => {
            cancelled = true;
            video.removeEventListener('loadedmetadata', handleLoadedMetadata);
            video.removeEventListener('error', handleError);
            video.pause();
            video.removeAttribute('src');
            video.load();
        };
    }, [url, mediaType]);

    return media;
};
